import { forwardRef } from "react";
import styles from "./index.module.css";
import type { SelectHTMLAttributes, ChangeEvent } from "react";

export type SelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
};

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, "onChange"> {
  options?: SelectOption[];
  placeholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  isLoading?: boolean;
  onChange?: (value: string | number) => void;
  className?: string;
  variant?: "white" | "transparent";
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      options = [],
      placeholder = "Select an option",
      emptyMessage = "No options available",
      loadingMessage = "Loading...",
      isLoading = false,
      onChange,
      className,
      value,
      variant = "white",
      ...props
    },
    ref,
  ) => {
    const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      // Convert to number if it's a valid number, otherwise keep as string
      const parsedValue = Number.isNaN(Number(value)) ? value : Number(value);
      onChange?.(parsedValue);
    };

    const selectClassName = [styles.select, className].filter(Boolean).join(" ");

    return (
      <select
        {...props}
        className={selectClassName}
        disabled={props.disabled || isLoading}
        onChange={handleChange}
        ref={ref}
        value={value}
        data-empty={!value}
        data-variant={variant}
      >
        {(() => {
          if (isLoading) {
            return (
              <option disabled value="">
                {loadingMessage}
              </option>
            );
          }

          if (options.length === 0) {
            return (
              <option disabled value="">
                {emptyMessage}
              </option>
            );
          }

          return (
            <>
              <option disabled value="">
                {placeholder}
              </option>
              {options.map((option) => (
                <option disabled={option.disabled} key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </>
          );
        })()}
      </select>
    );
  },
);
Select.displayName = "Select";
